import { AccountTypes } from '@interfaces/models/account-types';
import { IReceipt } from '@interfaces/models/receipt.interface';
import { SuffixValuesEnum } from '@interfaces/open-account/enums';
import {
  AANAccountType,
  IRAOptions,
  IRATypes,
  ISelectedAccount,
  ISelectedClientAccountsState,
  Move_To_New,
  TransactionTypesEnum,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { BeneRelationshipContent } from '@shared/services/content/content-jsons/bene-relationship-content';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { BeneficiariesState, Person } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import {
  getHasEligibleExistingAccount,
  getIraOption,
} from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import {
  getIsClient,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRAListedByRelationship,
  getIsIRASingleExecutor,
  getIsSingleExecutor,
  getIsSpouse,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';

import { IRATransferToType, joinWithSpace } from '../cashier-nonretirement';

/**
 * Returns a boolean to determines if a user will be shown the transfer-to page
 */
export function canChooseExisting(
  guideState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
) {
  return (
    getIsClient(guideState) &&
    getIsSpouse(guideState) &&
    getHasEligibleExistingAccount(selectedAccounts) &&
    getIraOption(selectedAccounts) === IRAOptions.ASSUME
  );
}

export function buildBeneByRelationshipReceipt(
  state: BeneficiariesState,
  triage: TriageState,
  personalInfo: IPersonalInfoClientState['clientData'],
  editable: boolean,
) {
  const isNotIRAAccount = !getIsIRAAccount(triage);
  const isNotIRAListedByRelationship = !getIsIRAListedByRelationship(triage);
  const isSpouse = getIsSpouse(triage);
  if (isNotIRAAccount || isNotIRAListedByRelationship || isSpouse) return null;

  const relationshipDesignation = BeneRelationshipContent.beneRelationshipOptions.find(
    (option) => option.key === state.relationshipDesignation,
  );
  const receipt: IReceipt = {
    header: 'Beneficiary designation information',
    editable: editable,
    editRoute: 'secure/inheritance/beneficiaries/details',
    sectionEvidenceID: 'beneByRelationship',
    rows: [
      {
        label: 'Designation',
        values: [relationshipDesignation?.value ?? ''],
        evidenceID: 'designation',
      },
      {
        label: 'Beneficiary (self)',
        values: [
          joinWithSpace([
            personalInfo.firstName,
            personalInfo.middleName,
            personalInfo.lastName,
            SuffixValuesEnum[personalInfo.suffix],
          ]).trim(),
        ],
        evidenceID: 'beneficiarySelf',
      },
    ],
  };
  state.otherBenes.forEach((bene: Person, i: number) => {
    const isDeceased = bene.dateGroup.dod ? ' (deceased)' : '';
    receipt.rows.push({
      label: `Beneficiary ${i + 1}${isDeceased}`,
      values: [
        bene.name,
        bene.dateGroup.dod
          ? `${bene.dateGroup.dob} - ${bene.dateGroup.dod}`
          : `${bene.dateGroup.dob}`,
      ],
      evidenceID: `beneficiary${i + 1}`,
      hideBorder: bene.children?.length > 0,
    });

    if (bene.children?.length > 0) {
      bene.children.forEach((child: Person, j: number) => {
        const isDeceased = child.dateGroup.dod ? ' (deceased)' : '';
        receipt.rows.push({
          label: `Child ${j + 1}${isDeceased}`,
          values: [
            child.name,
            child.dateGroup.dod
              ? `${child.dateGroup.dob} - ${child.dateGroup.dod}`
              : `${child.dateGroup.dob}`,
          ],
          evidenceID: `child${j + 1}`,
          hideBorder: j < bene.children.length - 1,
        });
      });
    }
  });

  return receipt;
}

export function buildAccountFromIRA(
  guideState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
  prospect: boolean,
  editable: boolean,
) {
  const edit = editable ? 'YES' === guideState.isSpouse : false;
  const iraOption = selectedAccounts.transferTo.iraOption;
  const iraType = selectedAccounts.transferTo.iraType;
  const isIRASingleExecutor = getIsIRASingleExecutor(guideState);

  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: edit,
    editRoute: 'secure/inheritance/ira-type',
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };
  let accountTypeValues = ['IRA'];

  if (iraType === IRATypes.ROTH) {
    accountTypeValues = ['Roth IRA'];
  } else if (iraType === IRATypes.INHERITED_ROTH) {
    accountTypeValues = ['Inherited Roth IRA'];
  } else if (iraType === IRATypes.INHERITED_OTHER) {
    accountTypeValues = ['Inherited Traditional IRA'];
  } else if (iraType === IRATypes.OTHER) {
    accountTypeValues = ['Traditional, rollover or SEP-IRA'];
  }

  if (!isIRASingleExecutor) {
    receipt.rows.push({
      label: 'Transfer Option',
      values: [IRAOptions.ASSUME === iraOption ? 'Assume' : 'Inherit'],
      evidenceID: 'transferOption',
    });
  }

  receipt.rows.push(
    {
      label: 'Account type',
      values: accountTypeValues,
      evidenceID: 'accountType',
    },
    {
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
  );
  return receipt;
}

function getAccountTypeToValue(transferTo: ISelectedClientAccountsState['transferTo']) {
  const OtherIRA = () =>
    IRAOptions.ASSUME === transferTo.iraOption ? 'Traditional IRA' : 'Inherited Traditional IRA';
  const RothIRA = () =>
    IRAOptions.ASSUME === transferTo.iraOption ? 'Roth IRA' : 'Inherited Roth IRA';

  if (IRATypes.ROTH === transferTo.iraType || IRATypes.INHERITED_ROTH === transferTo.iraType) {
    return RothIRA();
  }
  return OtherIRA();
}

function getExistingAccountTypeToValue(accountType: AANAccountType) {
  let assumeValue;
  switch (accountType) {
    case AANAccountType.TRADITIONAL_IRA:
      assumeValue = 'Traditional IRA';
      break;
    case AANAccountType.ROLLOVER_IRA:
      assumeValue = 'Rollover IRA';
      break;
    case AANAccountType.SEP_IRA_SINGLE_PARTICIPANT:
      assumeValue = 'SEP IRA';
      break;
    case AANAccountType.ROTH_IRA:
      assumeValue = 'Roth IRA';
      break;
    default:
      assumeValue = 'IRA';
      break;
  }
  return assumeValue;
}

export function buildAccountToIRA(
  triageState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
  openAccount: IOpenAccountState,
  editable: boolean,
) {
  const toAccount: ISelectedAccount = selectedAccounts.transferTo.selectedAccount;
  const edit = editable && canChooseExisting(triageState, selectedAccounts);
  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: edit || false,
    editRoute: edit ? 'secure/inheritance/transfer-to' : '',
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  const isIRAExecutor = getIsIRAExecutor(triageState);
  const isSingleExecutor = getIsSingleExecutor(triageState);
  const isIRAToEstate = isIRAExecutor && isSingleExecutor;

  if (isIRAToEstate) {
    receipt.rows.push({
      label: 'Account type',
      values: [
        IRATransferToType[selectedAccounts.transferTo.iraType] || 'Estate brokerage account',
      ],
      evidenceID: 'toAccountType',
    });

    return receipt;
  }

  if (selectedAccounts.transactionType === TransactionTypesEnum.EXISTING && toAccount) {
    // ONLY: client spouse assume existing
    receipt.rows.push(
      {
        label: 'Account type',
        values: [getExistingAccountTypeToValue(toAccount.accountData.accountType)],
        evidenceID: 'toAccountType0',
      },
      {
        label: 'Account number',
        values: [toAccount.accountNumber],
        evidenceID: 'toAccountNumber0',
      },
    );
  } else {
    receipt.rows.push(
      {
        label: 'Account type',
        values: [getAccountTypeToValue(selectedAccounts.transferTo)],
        evidenceID: 'toAccountType',
      },
      {
        label: 'Account number',
        values: [
          toAccount?.accountNumber === 'newAccount' ||
          !toAccount?.accountNumber ||
          openAccount.openAccountResponse?.isS24Reject
            ? 'Account number pending'
            : toAccount.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: 'divcapgains-modal',
        evidenceID: 'divAndCap',
      },
      {
        label: 'Settlement fund',
        values: ['Vanguard Federal Money Market Fund'],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: ReviewContent.fundInvestWarning,
      },
    );
  }

  return receipt;
}

export function buildDefaultBeneficiaryReceipt(
  triageState: TriageState,
  transactionType: TransactionTypesEnum,
  accountType: AccountTypes,
): IReceipt {
  const isIRASingleExecutor = getIsIRASingleExecutor(triageState);
  const receipt: IReceipt = {
    header: 'Beneficiaries',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'defaultBeneficiaries',
    rows: [
      {
        label: '',
        values: [
          `Assets will pass to your surviving spouse or your estate. This is the current default according to Vanguard\'s IRA Custodial Account Agreement.`,
        ],
        modal: ReceiptModal.BENEFICIARIES,
        evidenceID: 'defaultBene',
      },
    ],
  };

  return Move_To_New.has(transactionType) &&
    AccountTypes.IRA === accountType &&
    !isIRASingleExecutor
    ? receipt
    : null;
}
